import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs/internal/Observable';
import { environment } from '@app/../environments/environment';
import { CookieService } from '@vex/services/cookie.service';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { SessionDialogComponent } from '@vex/components/session-dialog/session-dialog.component';
import { HttpCancelService } from '@vex/http-interceptor/http-cancel.service';
import { ToastrService } from 'ngx-toastr';
import { HelperService } from '@vex/services/helper.service';
import { EnableFAComponent } from '@app/user-setting/enable-fa/enable-fa.component';
import { IdentitySource } from '@vex/model/user';
import { SyncService } from '@vex/http-service/sync.service';
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  baseUrl = environment.API_URL;
  errorData: {};
  userActivity: any;
  dialogRef: any;
  idleTime: number = 0;
  isShowExpiryMessage: boolean = false;
  isLoginPage: boolean = true;
  user: any = {};
  redirectUrl: string;
  readonly HEARTBEAT_KEY = 'LeaderHeartbeat';
  readonly LEADER_FLAG_KEY = 'LeaderExists';
  readonly HEARTBEAT_INTERVAL_MS = 5000;
  readonly HEARTBEAT_TIMEOUT_MS = 15000;
  readonly AUTH_MESSAGE_KEY = 'kumorai-auth-message';
  readonly SESSION_CONFIRMATION_KEY = 'sessionConfirmation';
  readonly SESSION_WARNING_DEADLINE_KEY = 'sessionWarningDeadline';
  readonly SESSION_WARNING_SECONDS = 30;
  heartbeatTimer: any;
  leaderCheckTimer: any;

  refreshInProgress = false;
  public bc: BroadcastChannel = null;
  private logoutInProgress = false;
  private suppressSessionDialogResult = false;

  constructor(
    private http: HttpClient,
    public cookie: CookieService,
    private router: Router,
    private dialog: MatDialog,
    private httpCancelService: HttpCancelService,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private syncService: SyncService,
    private ngZone: NgZone
  ) {
    if (typeof BroadcastChannel !== 'undefined') {
      this.bc = new BroadcastChannel('auth_channel');
      this.bc.onmessage = (ev) => this.handleAuthMessage(ev.data);
    }
    window.addEventListener('storage', (event) => {
      if (event.key !== this.AUTH_MESSAGE_KEY || !event.newValue) return;
      try {
        this.handleAuthMessage(JSON.parse(event.newValue).message);
      } catch {
        // Ignore malformed messages written by older application versions.
      }
    });
    if (!this.router.url.includes('/login') && this.router.url != '/') {
      this.tryBecomeLeader();
      this.leaderCheckTimer = setInterval(() => this.checkLeaderHealth(), 3000);
    }
    

    window.addEventListener('beforeunload', () => {
      if (this.isLeader()) {
        this.postAuthMessage({ type: 'leaderGone' });
        localStorage.removeItem(this.LEADER_FLAG_KEY);
        localStorage.removeItem(this.HEARTBEAT_KEY);
      }
      this.clearAllTimer();
    });
  }

  isLeader(): boolean {
    return localStorage.getItem(this.LEADER_FLAG_KEY) === this.getTabId();
  }

  getTabId(): string {
    let id = sessionStorage.getItem('TabId');
    if (!id) {
      id = `tab_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
      sessionStorage.setItem('TabId', id);
    }
    return id;
  }

  tryBecomeLeader() {
    const lastHeartbeat = Number(localStorage.getItem(this.HEARTBEAT_KEY)) || 0;
    const now = Date.now();
    const leaderFlag = localStorage.getItem(this.LEADER_FLAG_KEY);
    if (!leaderFlag || now - lastHeartbeat > this.HEARTBEAT_TIMEOUT_MS) {
      localStorage.setItem(this.LEADER_FLAG_KEY, this.getTabId());
      this.startHeartbeat();
      this.startUserInactivityCheck();
    }
  }

  startHeartbeat() {
    this.stopHeartbeat();
    localStorage.setItem(this.HEARTBEAT_KEY, Date.now().toString());
    this.heartbeatTimer = setInterval(() => {
      if (!this.isLeader()) {
        this.stopHeartbeat();
        this.stopUserInactivityCheck();
        return;
      }
      localStorage.setItem(this.HEARTBEAT_KEY, Date.now().toString());
    }, this.HEARTBEAT_INTERVAL_MS);
  }

  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = undefined;
    }
  }

  checkLeaderHealth() {
    const leader = localStorage.getItem(this.LEADER_FLAG_KEY);
    const lastHeartbeat = Number(localStorage.getItem(this.HEARTBEAT_KEY)) || 0;
    const now = Date.now();

    if (!leader) {
      this.tryBecomeLeader();
      return;
    }

    if (now - lastHeartbeat > this.HEARTBEAT_TIMEOUT_MS) {
      this.tryBecomeLeader();
    } else {
      if (!this.isLeader()) {
        this.stopHeartbeat();
        this.stopUserInactivityCheck();
      }
    }
  }

  handleUserActivity() {
    if (!this.isLoggedIn()) return;

    // Activity in any tab/window belongs to the shared browser session. It must
    // also cancel a warning opened by a background leader tab.
    this.applySharedActivity(Date.now(), true);
  }

  getRefreshToken(): Promise<any> {
    const refreshToken = this.cookie.getCookie('_Kumorai-RefreshToken');
    if (!refreshToken) return Promise.reject('No refresh token found');
    const info = JSON.parse(localStorage.getItem('Info') || 'null');
    const organizationId = info?.OrganizationId || info?.organizationId || null;

    return this.http
      .post<any>(`${this.baseUrl}Account/RefreshToken`, { Token: refreshToken, OrganizationId: organizationId })
      .toPromise()
      .then((response) => {
        if (response && response.Token && response.RefreshToken) {
          return response;
        }
        return Promise.reject('Refresh token expired or invalid');
      })
      .catch((err) => Promise.reject(err));
  }

  async refreshToken() {
    if (!this.isLeader()) return;
    if (this.refreshInProgress) return;
    if (localStorage.getItem('IsRefreshTokenCall') === 'true') return;

    this.refreshInProgress = true;
    localStorage.setItem('IsRefreshTokenCall', 'true');

    try {
      const user = await this.getRefreshToken();
      if (user && user.RefreshToken) {
        this.storeLatestUserData(user);
        this.postAuthMessage({ type: 'refreshSuccess', user });
      } else {
        this.afterSessionExpired();
      }
    } catch (err) {
      console.log(err);
      this.afterSessionExpired();
    } finally {
      this.refreshInProgress = false;
      localStorage.setItem('IsRefreshTokenCall', 'false');
    }
  }

  enable2FA(PhoneNumber) {
    return new Promise((success) => {
      this.dialog
        .open(EnableFAComponent, {
          width: '40%',
          disableClose: true,
          data: { PhoneNumber: PhoneNumber, Heading: 'Login' }
        })
        .afterClosed()
        .subscribe((r) => {
          success(r === true ? true : false);
        });
    });
  }

  async storeLatestUserData(user, isLogin: boolean = false, data: any = {}) {
    if (isLogin) {
      this.logoutInProgress = false;
      this.clearSessionWarningState();
      this.tryBecomeLeader();
      if (!this.leaderCheckTimer) {
        this.leaderCheckTimer = setInterval(() => this.checkLeaderHealth(), 3000);
      }
    }
    localStorage.setItem('token', user['Token']);
    this.applyTokenOrganizationContext(user);
    if (isLogin && data?.payload?.['Source'] !== undefined && user['Source'] === undefined) {
      user['Source'] = data.payload['Source'];
    }
    if (isLogin) {
      if (
        data?.payload?.['Source'] == 1 &&
        user['Email'] != 'farjad@kumorai.io' &&
        user['Email'] != 'farjad@kumosoft.org' &&
        user['UserName'] != 'farjad@kumorai.io' &&
        environment.CLIENT_NAME != 'Development'
      ) {
        this.helperService.loadingSpinner = false;
        const status = await this.enable2FA(user.PhoneNumber);
        if (!status) {
          data.disabledButton = false;
          data.disabledAzureButton = false;
          this.helperService.loadingSpinner = false;
          return;
        }
      }
    }

    const rights: any = { FeatureIds: [], ActionIds: [] };
    (user.RoleArray || []).forEach((role) => {
      if (role.RoleAuthRules) {
        role.RoleAuthRules.forEach((access) => {
          if (!rights.FeatureIds.includes(access.FeatureId) && access.PageAccess) {
            rights.FeatureIds.push(access.FeatureId);
          }
          if (access.ActionIds) {
            access.ActionIds.forEach((action) => {
              if (!rights.ActionIds.includes(action)) rights.ActionIds.push(action);
            });
          }
        });
      }
    });

    user['UserRights'] = rights;
    localStorage.setItem('token', user['Token']);
    localStorage.setItem('IdleTime', Date.now().toString());
    localStorage.setItem(
      'SessionTime',
      (user['SessionTime'] ? Number(user['SessionTime']) : 900).toString()
    );
    localStorage.setItem('Info', JSON.stringify(user));
    localStorage.setItem('IsRefreshTokenCall', 'false');
    // Tenant switch responses only replace the access token; keep the existing refresh cookie untouched.
    if (user['RefreshToken']) {
      this.storeRefreshToken(user['RefreshToken'], user['SessionTime'] ? Number(user['SessionTime']) : 900);
    }
    delete user['RefreshToken'];
    delete user['Token'];
    this.isShowExpiryMessage = true;
    this.user = user;
    if (isLogin) {
      // Login can return to the same route/component instance, so lifecycle
      // hooks alone cannot guarantee startup recovery. The service deduplicates
      // this explicit trigger with root/layout startup calls.
      this.syncService.resumeOnApplicationStart().catch(error =>
        console.warn('Platform data login recovery could not complete.', error));
    }
  }

  applyTokenOrganizationContext(user: any) {
    const tokenPayload = this.decodeJwtPayload(user?.['Token']);
    if (!tokenPayload) {
      return;
    }

    user['OrganizationId'] = tokenPayload['OrganizationId'] || user['OrganizationId'];
    user['HomeOrganizationId'] = tokenPayload['HomeOrganizationId'] || user['HomeOrganizationId'];
    user['TenantSwitchSource'] = tokenPayload['TenantSwitchSource'] || user['TenantSwitchSource'];
    user['ImpersonatedByUserId'] = tokenPayload['ImpersonatedByUserId'] || user['ImpersonatedByUserId'];
    user['ImpersonatedByUsername'] = tokenPayload['ImpersonatedByUsername'] || user['ImpersonatedByUsername'];
  }

  decodeJwtPayload(token: string): any {
    if (!token) {
      return null;
    }

    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }

    try {
      let paddedBase64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      while (paddedBase64.length % 4) {
        paddedBase64 += '=';
      }
      return JSON.parse(atob(paddedBase64));
    } catch {
      return null;
    }
  }

  SendLoginRequest(userParams: any): any {
    const options = { headers: new HttpHeaders().set('Content-Type', 'application/json') };
    return this.http.post(this.baseUrl + 'Account/Login', userParams, options);
  }

  storeRefreshToken(refreshToken: string, expiresIn: number = 900) {
    const buffer = 300;
    const refreshExpiry = new Date(Date.now() + expiresIn * 1000);
    const earlyExpiry = new Date(Date.now() + (expiresIn - buffer) * 1000);

    this.cookie.setCookie({ name: '_Kumorai-RefreshToken', value: refreshToken, expireDate: refreshExpiry });
    this.cookie.setCookie({ name: '_Kumorai-ExpireToken', value: earlyExpiry, expireDate: earlyExpiry });
  }

  revokeToken(refreshToken): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'Account/RevokeToken', { Token: refreshToken });
  }

  /**
   * Checks shared browser authentication state without redirecting the caller.
   *
   * Root application startup uses this passive check so a newly opened tab can
   * resume durable platform-data work before its authenticated layout is built.
   */
  hasPersistedSession(): boolean {
    const refreshToken = this.cookie.getCookie('_Kumorai-RefreshToken');
    const token = localStorage.getItem('token');
    const info = this.getUserInfo();
    return !!(token && token !== '' && info && refreshToken);
  }

  isLoggedIn(): boolean {
    let status = this.hasPersistedSession();
    if (this.router.url.includes('register')) {
      return true;
    }
    if(!status) {
      this.router.navigate(['login'], { queryParams: { returnUrl: this.redirectUrl } });
    }
    return status;
  }

  getRights(type, id) {
    let isAllow = false;
    const info = JSON.parse(localStorage.getItem('Info') || 'null');
    if (info && info.UserRights?.[type]?.find((x) => x == id)) isAllow = true;
    return isAllow;
  }

  getUserInfo() {
    const currentUser = JSON.parse(localStorage.getItem('Info') || 'null');
    return currentUser;
  }

  getUserProperty(name: string, type: string = '') {
    const currentUser = JSON.parse(localStorage.getItem('Info') || 'null');
    return currentUser ? currentUser[name] : undefined;
  }

  isNotRequired2FA() {
    let status = false;
    let Email = this.getUserProperty('Email');
    let UserName = this.getUserProperty('UserName');
    if (
      Email == 'farjad@kumorai.io' || 
      UserName == 'farjad@kumorai.io' ||
      Email == 'farjad@kumosoft.org' ||
      environment.CLIENT_NAME == 'Development'
    ) {
      status = true;
    }
    return status;
  }

  getIdentitySource(): IdentitySource {
    const sourceValue = this.getUserProperty('Source')
      ?? this.getUserProperty('IdentitySource')
      ?? this.getUserProperty('AuthenticationSource')
      ?? this.getUserProperty('LoginType')
      ?? this.getUserProperty('UserType');

    if (typeof sourceValue === 'number') {
      return sourceValue === IdentitySource.ActiveDirectory
        ? IdentitySource.ActiveDirectory
        : IdentitySource.LocalUser;
    }

    const normalizedSource = (sourceValue || '').toString().trim().toLowerCase();
    return normalizedSource === IdentitySource.ActiveDirectory.toString()
      || normalizedSource === 'activedirectory'
      || normalizedSource === 'active directory'
      || normalizedSource === 'azuread'
      || normalizedSource === 'azure ad'
      || normalizedSource === 'entra'
      || normalizedSource === 'entra id'
      ? IdentitySource.ActiveDirectory
      : IdentitySource.LocalUser;
  }

  deleteCookie() {
    this.cookie.deleteCookie('_Kumorai-RefreshToken');
    this.cookie.deleteCookie('_Kumorai-ExpireToken');
  }

  /**
   * Signs out this browser session and notifies other tabs.
   * Authentication state is always removed; clearLocalData controls only the
   * shared global platform cache. Cache deletion must be an explicit user
   * choice; automatic expiry and timeout paths preserve IndexedDB so the next
   * login can continue from the persisted synchronization checkpoints.
   */
  async logout(
    isRevokeToken: boolean = false,
    clearLocalData: boolean = false,
    userInitiated: boolean = false
  ) {
    if (userInitiated) {
      // A completed login starts a new session. Do not let a stale logout guard
      // from an earlier session suppress an explicit user action.
      this.logoutInProgress = false;
    }
    if (this.logoutInProgress) {
      return;
    }
    try {
      this.postAuthMessage({ type: 'logout-started', clearLocalData });
    } catch (error) {
      console.error('Unable to notify other tabs about logout.', error);
    }
    await this.performLogout(isRevokeToken, clearLocalData, true);
  }

  private handleAuthMessage(data: any): void {
    if (!data || !data.type) return;

    switch (data.type) {
      case 'refreshSuccess':
        if (data.user) this.storeLatestUserData(data.user);
        break;
      case 'leaderGone':
        this.tryBecomeLeader();
        break;
      case 'idleUpdate':
        if (data.time) this.applySharedActivity(Number(data.time), false);
        break;
      case 'session-warning':
        if (
          data.deadline &&
          Number(localStorage.getItem(this.SESSION_WARNING_DEADLINE_KEY) || '0') === Number(data.deadline)
        ) {
          this.confirmation(Number(data.deadline));
        }
        break;
      case 'logout-started':
        this.performLogout(false, !!data.clearLocalData, false);
        break;
      default:
        break;
    }
  }

  private postAuthMessage(message: any): void {
    this.bc?.postMessage(message);
    localStorage.setItem(this.AUTH_MESSAGE_KEY, JSON.stringify({
      id: `${this.getTabId()}_${Date.now()}_${Math.random()}`,
      message
    }));
  }

  private async performLogout(
    isRevokeToken: boolean,
    clearLocalData: boolean,
    clearSharedCache: boolean
  ): Promise<void> {
    if (this.logoutInProgress) {
      return;
    }
    this.logoutInProgress = true;
    const token = this.cookie.getCookie('_Kumorai-RefreshToken');
    if (token && isRevokeToken) {
      this.revokeToken(token).subscribe(() => {});
    }
    this.httpCancelService.cancelPendingRequests();
    try {
      await this.syncService.prepareForLogout(clearLocalData && clearSharedCache, clearSharedCache);
    } catch (error) {
      console.error('Unable to finish local sync cleanup during logout.', error);
    } finally {
      this.deleteCookie();
      this.clearAllTimer();
      localStorage.clear();
      sessionStorage.clear();
      if (!this.router.url.includes('/login')) {
        await this.router.navigate(['/login'], { queryParams: { returnUrl: this.redirectUrl } });
      }
    }
  }

  clearAllTimer() {
    this.stopUserInactivityCheck();
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = undefined;
    }
    if (this.leaderCheckTimer) {
      clearInterval(this.leaderCheckTimer);
      this.leaderCheckTimer = undefined;
    }
  }

  private stopUserInactivityCheck(): void {
    if (this.userActivity) {
      clearInterval(this.userActivity);
      this.userActivity = undefined;
    }
  }

  startUserInactivityCheck() {
    if (!this.isLeader()) return;
    this.ngZone.runOutsideAngular(() => {
      if (this.userActivity) clearInterval(this.userActivity);

      this.userActivity = setInterval(() => {
        const idleTime = Number(localStorage.getItem('IdleTime') || '0');
        const currentTime = Date.now();
        const diffTime = (currentTime - idleTime) / 1000;
        const sessionTime = Number(localStorage.getItem('SessionTime')) || 900;
        if (idleTime > 0 && diffTime >= Math.max(0, sessionTime - this.SESSION_WARNING_SECONDS) && this.isLoggedIn()) {
          this.startSessionWarning(idleTime + sessionTime * 1000);
        }
        const seconds = this.getTokenExipryTimeLeft('_Kumorai-ExpireToken');
        if (
          seconds < 30 &&
          (!localStorage.getItem('IsRefreshTokenCall') ||
            localStorage.getItem('IsRefreshTokenCall') === 'false')
        ) {
          this.refreshToken();
        }
      }, 3000);
    });
  }

  /** Starts one shared warning and asks every open tab to display it. */
  private startSessionWarning(inactivityDeadline: number): void {
    let deadline = Number(localStorage.getItem(this.SESSION_WARNING_DEADLINE_KEY) || '0');
    if (deadline !== inactivityDeadline) {
      deadline = inactivityDeadline;
      localStorage.setItem(this.SESSION_WARNING_DEADLINE_KEY, deadline.toString());
      localStorage.setItem(this.SESSION_CONFIRMATION_KEY, 'true');
      this.postAuthMessage({ type: 'session-warning', deadline });
    }
    this.confirmation(deadline);
  }

  /** Displays the shared absolute-deadline warning at most once in this tab. */
  confirmation(deadline: number = Date.now() + this.SESSION_WARNING_SECONDS * 1000) {
    if (!this.isLoggedIn() || this.dialogRef) return;

    this.ngZone.run(() => {
      this.dialogRef = this.dialog.open(SessionDialogComponent, {
        panelClass: 'custom-dialog-container',
        disableClose: true,
        data: { deadline }
      });

      localStorage.setItem(this.SESSION_CONFIRMATION_KEY, 'true');
      localStorage.setItem(this.SESSION_WARNING_DEADLINE_KEY, deadline.toString());

      this.dialogRef.afterClosed().subscribe((dialogResult) => {
        this.dialogRef = null;
        if (this.suppressSessionDialogResult) {
          this.suppressSessionDialogResult = false;
          return;
        }
        if (dialogResult === true) {
          this.applySharedActivity(Date.now(), true);
        } else if (dialogResult === false) {
          // Automatic inactivity logout preserves IndexedDB. Clearing cached
          // platform data remains available only from explicit user sign-out.
          this.logout(false, false);
        }
      });
    });
  }

  /** Applies activity received locally or from another tab and closes warnings. */
  private applySharedActivity(activityTime: number, broadcast: boolean): void {
    if (!Number.isFinite(activityTime)) return;

    const currentIdleTime = Number(localStorage.getItem('IdleTime') || '0');
    if (activityTime < currentIdleTime) return;

    const latestActivityTime = Math.max(currentIdleTime, activityTime);
    localStorage.setItem('IdleTime', latestActivityTime.toString());
    this.clearSessionWarningState();

    if (this.dialogRef) {
      this.suppressSessionDialogResult = true;
      this.dialogRef.close(true);
    }
    if (broadcast) {
      this.postAuthMessage({ type: 'idleUpdate', time: latestActivityTime });
    }
    if (this.isLeader() && !this.userActivity) {
      this.startUserInactivityCheck();
    }
  }

  /** Clears current and legacy warning state without touching authentication or cached data. */
  private clearSessionWarningState(): void {
    localStorage.removeItem(this.SESSION_CONFIRMATION_KEY);
    localStorage.removeItem(this.SESSION_WARNING_DEADLINE_KEY);
    localStorage.removeItem('sessionTimeOut');
  }

  afterSessionExpired() {
    if (!this.router.url.includes('/login')) {
      this.toastrService.info('Unable to fetch token. Please Login again');
    }
    // Authentication/session failures must not delete the shared IndexedDB cache.
    this.logout(false, false);
  }

  getTokenExipryTimeLeft(key: string): number {
    let seconds = 0;
    const expireToken = this.cookie.getCookie(key);
    if (expireToken) {
      const currentTime = Date.now();
      const userActiveTime = new Date(expireToken).getTime();
      const difference_In_Time = userActiveTime - currentTime;
      seconds = difference_In_Time / 1000;
    }
    return seconds;
  }
}
